import { useState, useEffect } from 'react';
import { publicAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  MessageCircle,
  Phone,
  ExternalLink,
  Loader2,
  Send,
  Flame
} from 'lucide-react';

export default function About() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await publicAPI.getSiteSettings();
        setSettings(res.data);
      } catch (error) {
        console.error('Failed to fetch site settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 size={32} className="animate-spin text-[#1877F2]" />
      </div>
    );
  }

  const contactLinks = [
    { 
      label: 'Zalo', 
      url: settings?.zalo_link, 
      icon: MessageCircle, 
      color: '#0068FF', 
      bg: '#E6F0FF' 
    },
    { 
      label: 'Facebook', 
      url: settings?.facebook_link, 
      icon: () => (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      ),
      color: '#1877F2', 
      bg: '#E7F3FF' 
    },
    { 
      label: 'Telegram', 
      url: settings?.telegram_link, 
      icon: Send, 
      color: '#0088CC', 
      bg: '#E3F2FD' 
    },
    { 
      label: 'Hotline', 
      url: settings?.hotline ? `tel:${settings.hotline}` : null, 
      displayText: settings?.hotline,
      icon: Phone, 
      color: '#31A24C', 
      bg: '#EBF5ED' 
    },
  ].filter(link => link.url || link.displayText);

  return (
    <div className="space-y-6" data-testid="about-page">
      {/* Header */}
      <div className="animate-fadeIn text-center py-6">
        {settings?.logo_url ? (
          <img 
            src={settings.logo_url} 
            alt={settings.site_name || 'Logo'} 
            className="h-20 w-20 mx-auto rounded-2xl object-cover mb-4"
            data-testid="about-logo"
          />
        ) : (
          <div className="h-20 w-20 mx-auto rounded-2xl bg-gradient-to-br from-[#1877F2] to-[#E41E3F] flex items-center justify-center mb-4" data-testid="about-logo-default">
            <Flame size={40} className="text-white" />
          </div>
        )}
        <h1 className="text-2xl font-bold text-[#1C1E21]">
          {settings?.site_name || 'Hotface Analytics'}
        </h1>
        <p className="text-[#606770] mt-2 max-w-lg mx-auto">
          {settings?.about_text || 'Hệ thống phân tích tương tác Facebook thông minh'}
        </p>
      </div>

      {/* Contact Cards */}
      {contactLinks.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto animate-fadeIn stagger-1">
          {contactLinks.map((link) => {
            const IconComponent = link.icon;
            return (
              <Card 
                key={link.label} 
                className="hover:shadow-md transition-shadow cursor-pointer"
                data-testid={`contact-${link.label.toLowerCase()}`}
              >
                <CardContent className="pt-6">
                  <a 
                    href={link.url || '#'} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-4"
                  >
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: link.bg, color: link.color }}
                    >
                      <IconComponent size={24} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[#1C1E21]">{link.label}</p>
                      <p className="text-sm text-[#606770] truncate">
                        {link.displayText || link.url}
                      </p>
                    </div>
                    <ExternalLink size={16} className="text-[#8C939D] flex-shrink-0" />
                  </a>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="max-w-2xl mx-auto">
          <CardContent className="pt-6 text-center text-[#606770]">
            Thông tin liên hệ chưa được cấu hình. Admin vui lòng cập nhật trong phần Quản trị.
          </CardContent>
        </Card>
      )}

      {/* Premium Footer */}
      <div className="mt-20 pb-10 animate-fadeIn stagger-2">
        <div className="flex flex-col items-center justify-center">
          <div className="w-16 h-[2px] bg-gradient-to-r from-transparent via-[#1877F2]/30 to-transparent mb-6"></div>
          
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-white shadow-sm border border-[#F0F2F5] flex items-center justify-center">
              <Flame size={20} className="text-[#1877F2]" />
            </div>
            <div className="text-left">
              <p className="text-sm font-bold text-[#1C1E21] tracking-tight">
                {settings?.site_name || 'Hotface Analytics'}
              </p>
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#1877F2] opacity-80">
                Premium Engine
              </p>
            </div>
          </div>

          <div className="px-6 py-2 rounded-full bg-white/50 backdrop-blur-sm border border-[#F0F2F5] shadow-sm">
            <p className="text-[11px] font-medium text-[#606770] whitespace-pre-wrap text-center">
              {settings?.footer_copyright || "@ 2026 TriTechz. All rights reserved."}
            </p>
          </div>

          <div className="flex items-center gap-6 mt-6">
            <a href="#" className="text-[10px] font-bold text-[#8C939D] hover:text-[#1877F2] transition-colors tracking-wider">PRIVACY</a>
            <a href="#" className="text-[10px] font-bold text-[#8C939D] hover:text-[#1877F2] transition-colors tracking-wider">TERMS</a>
            <a href="#" className="text-[10px] font-bold text-[#8C939D] hover:text-[#1877F2] transition-colors tracking-wider">SUPPORT</a>
          </div>
        </div>
      </div>
    </div>
  );
}
